import { Building2, ExternalLink, Shield } from 'lucide-react';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { SortableTh } from '../common/SortableTh';
import { isClientShielded } from '../../utils/shieldPlatinum';
import type { SortDir } from '../../utils/tableSort';
import type { BvOrganisation } from '../../types/billValidationTypes';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 border-green-200',
  onboarding: 'bg-blue-50 text-blue-700 border-blue-200',
  lead: 'bg-gray-50 text-gray-600 border-gray-200',
};

export type ClientSortField = 'name' | 'abn' | 'broker' | 'shield' | 'sites' | 'status' | 'created';

type Props = {
  // Already searched, filtered and sorted by the page — this renders rows.
  organisations: BvOrganisation[];
  loading: boolean;
  sortField: ClientSortField | null;
  sortDir: SortDir;
  onSort: (field: ClientSortField) => void;
  onSelect: (org: BvOrganisation) => void;
  onOpenNewWindow: (org: BvOrganisation) => void;
};

export function OrganisationTable({
  organisations, loading, sortField, sortDir, onSort, onSelect, onOpenNewWindow,
}: Props) {
  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (organisations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Building2 className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm">No entities found</p>
      </div>
    );
  }

  const th = (label: string, field: ClientSortField) => (
    <SortableTh label={label} field={field} sortField={sortField} sortDir={sortDir} onSort={onSort} />
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs text-gray-500 font-medium uppercase tracking-wider">
            {th('Name', 'name')}
            {th('ABN', 'abn')}
            {th('Broker', 'broker')}
            {th('Shield Platinum', 'shield')}
            {th('Sites', 'sites')}
            {th('Status', 'status')}
            {th('Created', 'created')}
            <th className="px-4 py-3 w-8" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {organisations.map((org) => (
            <tr
              key={org.id}
              // Cmd/Ctrl-click and middle-click open a new tab. A table row cannot
              // be an anchor, so without this the row navigates in place however it
              // is clicked, and opening an entity alongside the list meant reaching
              // for the icon at the end of the row.
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey) { e.preventDefault(); onOpenNewWindow(org); return; }
                onSelect(org);
              }}
              onAuxClick={(e) => {
                if (e.button === 1) { e.preventDefault(); onOpenNewWindow(org); }
              }}
              className="hover:bg-blue-50/40 cursor-pointer transition-colors"
              title="Cmd/Ctrl-click to open in a new tab"
            >
              <td className="px-4 py-3 font-medium text-gray-900">
                {org.name}
                {/* Job 28: without this, a deactivated client looks identical to an
                    active one whenever the Bill Validation filter shows them. */}
                {org.bill_val !== 'active' && (
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-500">
                    Deactivated
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-500 font-mono text-xs">{org.abn ?? '—'}</td>
              <td className="px-4 py-3 text-gray-600">{org.broker ?? '—'}</td>
              <td className="px-4 py-3">
                {isClientShielded(org) ? (
                  <Shield className="h-4 w-4 text-blue-600" />
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-600">{org.site_count ?? 0}</td>
              <td className="px-4 py-3">
                <Badge
                  variant="outline"
                  className={cn('text-xs capitalize', STATUS_STYLES[org.status] ?? '')}
                >
                  {org.status}
                </Badge>
              </td>
              <td className="px-4 py-3 text-gray-400 text-xs">
                {new Date(org.created_at).toLocaleDateString('en-AU')}
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenNewWindow(org); }}
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                  title="Open in new window"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
